import { EventEmitter, Inject, Injectable, isDevMode } from "@angular/core";
import * as Sentry from "@sentry/angular"
import { Integrations } from '@sentry/tracing';
import { Config } from "./error-handler.module";
import { environment } from 'src/environments/environment';
import { DEV_ENV_NAME } from '../constants/env';
import { IBackendError } from './backend-error';
import { IHttpRequestError } from '../httpconfig.interceptor';
import { isConnectivityFailure } from './connectivity';
import { restoreIonicFrames } from './ionic-frames';

export interface Options extends Sentry.ErrorHandlerOptions { }

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService extends Sentry.SentryErrorHandler {
  // onError = new EventEmitter();

  public static skipErrorLog(error: any): boolean {
    // An error that already put a fault number on the user's screen has to reach Sentry,
    // whatever it says: the number is all support gets from the report, and dropping the
    // event makes it unsearchable. That is how #336 arrived - an error id from a customer
    // that matched nothing, because the connectivity markers below had thrown the event
    // away before it was sent.
    if (error && error.faultNumber) {
      return false;
    }

    let message = "";
    if (error instanceof Error) {
      message = error.message;
    } else if (typeof error === 'object') {
      message = JSON.stringify(error);
    } else {
      message = String(error);
    }

    return isConnectivityFailure(message);
  }

  // Both are rejected and caught inside @microsoft/signalr: the keep-alive ping hitting a socket
  // that died while the WebView was frozen, and an automatic reconnect attempt cut short by our
  // own stop() on logout. zone.js reports them anyway, because the library awaits a promise that
  // was rejected before the await attached to it (#316, #350). The stop() message with
  // _startWithStateTransitions in the stack is the app's own start() racing stop(), fixed in #316 -
  // keep it. The reconnect case is told by that frame missing rather than by _reconnect being
  // there, because the iOS WebView leaves async frames out of the stack.
  private static isSignalRInternalRejection(error: any): boolean {
    if (error === 'WebSocket is not in the OPEN state') {
      return true;
    }

    return error instanceof Error
      && error.message === 'Failed to start the HttpConnection before stop() was called.'
      && String(error.stack).indexOf('_startWithStateTransitions') === -1;
  }

  constructor(
    @Inject('config') config?: Config,
  ) {
    super(config.options);
    this._options.extractor = (e: any) => e.rejection || this._defaultExtractor(e);
    const release = config.release && (typeof config.release === 'string' ? config.release : config.release());

    if (environment.envName === DEV_ENV_NAME) {
      console.log({
        release,
        environment: config.environment,
        dsn: config.dsn,
        integrations: config.browserTracking !== false ? [
          new Integrations.BrowserTracing(),
        ] : [],
        tracesSampleRate: config.tracesSampleRate,
        beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
          try {
            if (hint!.originalException instanceof Error) {
              const err = hint.originalException as Error;

              // disable sentry log of errors that cannot be handled in handleError
              if (ErrorHandlerService.skipErrorLog(err)) {
                return null;
              }
            }
          } catch (err) {}
          return event;
        },
      });
    }

    Sentry.init({
      release,
      environment: config.environment,
      dsn: config.dsn,
      integrations: config.browserTracking !== false ? [
        new Integrations.BrowserTracing(),
      ] : [],
      tracesSampleRate: config.tracesSampleRate,
      beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
        try {
          restoreIonicFrames(event);

          if (hint && ErrorHandlerService.isSignalRInternalRejection(hint.originalException)) {
            return null;
          }

          if (hint!.originalException instanceof Error) {
            const err = hint.originalException as Error;

            // disable sentry log of errors that cannot be handled in handleError
            if (ErrorHandlerService.skipErrorLog(err)) {
              return null;
            }

            // The fault number is the id shown to the user in the error popup, so a
            // number read off a screenshot can be looked up as fault_number:"<uuid>".
            // Tagged here rather than in handleError because these errors reach Sentry
            // through the SDK's browser instrumentation, which never calls it.
            const faultNumber = (err as any).faultNumber;
            if (faultNumber) {
              event.tags = event.tags || {};
              event.tags.fault_number = faultNumber;
            }

            // A request error nobody handled is captured by the SDK's own setTimeout wrapper,
            // which rxjs rethrows it into, so the event knows nothing of the request unless it
            // is put here (#372).
            const request = err as IHttpRequestError;
            if (request.url) {
              event.tags = event.tags || {};
              event.tags.http_status = String(request.status);
              event.tags.request_url = request.url.split('?')[0].substring(0, 200);
            }

            // Errors that only relay a backend message group by that message instead of by
            // the frontend frame that relayed it: Sentry's default put every backend defect
            // into one bucket, and the localized text wrapped around the message split each
            // of them again per language (#320).
            const backendKey = (err as IBackendError).backendErrorKey;
            if (backendKey) {
              event.fingerprint = ["backend-error", backendKey];
              event.tags = event.tags || {};
              event.tags.backend_error = backendKey;
            }

            // What the caller knows about the failure beyond the message, kept out of it so the group stays whole (#379).
            const sentryTags = (err as any).sentryTags;
            if (sentryTags) {
              event.tags = { ...event.tags, ...sentryTags };
            }
          }
        } catch (err) {}
        return event;
      },
    });
  }

  setShowDialog(show: boolean) {
    this._options.showDialog = show;
  }
  
  handleError(error) {
    if (!ErrorHandlerService.skipErrorLog(error)) {
      // this.onError.emit(error);
      Sentry.withScope(scope => {
        scope.setExtra("errorExtraData", error);
        scope.setExtra("handlerSource", "service");
        if (environment.envName === DEV_ENV_NAME) {
          console.error("TSTERR handleError", error);
        } else {
          super.handleError(error);
        }
      });
    }

    // do not catch in dev mode
    // if (isDevMode()) {
    //   console.error(error);
    // } else {
    //   super.handleError(error);
    // }
  }
}
